import {
  Alert,
  Container,
  Group,
  Table as MantineTable,
  Pagination,
  ScrollArea,
  Stack,
  Switch,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Checkbox,
} from "@mantine/core"
import { IconInfoCircle } from "@tabler/icons-react"
import { flexRender, Table as TableProps } from "@tanstack/react-table"
import { Card } from "@tfg/ui"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import classes from "~/assets/css/Table.module.css"

import { SortIcon } from "./SortIcon"

type CheckboxSelectionProps<T> = {
  selectedRows: Set<string>
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<string>>>
  getRowId: (row: T) => string
}

type Props<T> = {
  table: TableProps<T>
  onRowClick?: (row: T) => void
  noColumnWrap?: boolean
  view?: string
  showOnlyMainFabric?: boolean
  setShowOnlyMainFabric?: React.Dispatch<React.SetStateAction<boolean>>
  pagination?: boolean
  manualPaginationCount?: number
  pageIndex?: number
  checkboxSelection?: CheckboxSelectionProps<T>
}

export const Table = <T,>({
  table,
  onRowClick,
  noColumnWrap = false,
  view,
  showOnlyMainFabric,
  setShowOnlyMainFabric = () => undefined,
  pagination = true,
  manualPaginationCount,
  pageIndex,
  checkboxSelection,
}: Props<T>) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (pageIndex !== undefined && pageIndex === 0) {
      setCurrentPage(1)
    } else if (pageIndex !== undefined && pageIndex > 0) {
      setCurrentPage(pageIndex + 1 || 1)
    }
  }, [pageIndex])

  const [currentPage, setCurrentPage] = useState(1)
  const handleChangePage = useCallback(
    (page: number) => {
      setCurrentPage(page)
      table.setPageIndex(page - 1)
    },
    [table]
  )

  const tableState = table.getState()
  const hasFilters = useMemo(
    () => tableState.globalFilter || tableState.columnFilters.length > 0,
    [tableState.globalFilter, tableState.columnFilters.length]
  )
  const handleRowSelect = useCallback(
    (rowId: string) => {
      if (checkboxSelection) {
        checkboxSelection.setSelectedRows((prev) => {
          const newSet = new Set(prev)
          if (newSet.has(rowId)) {
            newSet.delete(rowId)
          } else {
            newSet.add(rowId)
          }
          return newSet
        })
      }
    },
    [checkboxSelection]
  )

  return (
    <Stack>
      <Group grow>
        <Group justify="left">
          <Text fw={700} size="sm">
            {t("count", {
              count: manualPaginationCount
                ? manualPaginationCount
                : table.getFilteredRowModel().rows.length,
            })}
          </Text>
          {view === "traceability" && (
            <Switch
              label={t("showOnlyMainFabric")}
              checked={showOnlyMainFabric}
              onChange={() => setShowOnlyMainFabric((v) => !v)}
            />
          )}
        </Group>
        {pagination ? (
          <Group justify="right">
            <Pagination
              size="sm"
              value={currentPage}
              total={table.getPageCount()}
              onChange={handleChangePage}
            />
          </Group>
        ) : null}
      </Group>
      <Card p="xs">
        <ScrollArea
          type="scroll"
          style={{ whiteSpace: noColumnWrap ? "nowrap" : "inherit" }}
        >
          <MantineTable
            stickyHeader
            stickyHeaderOffset={60}
            style={{ height: 1 }}
            borderColor="var(mantine-colors-gray-3)"
            withRowBorders
            classNames={classes}
          >
            <TableThead>
              <TableTr>
                {checkboxSelection && (
                  <TableTh>
                    <Checkbox
                      checked={
                        checkboxSelection.selectedRows.size ===
                        table.getCoreRowModel().rows.length
                      }
                      indeterminate={
                        checkboxSelection.selectedRows.size > 0 &&
                        checkboxSelection.selectedRows.size <
                          table.getCoreRowModel().rows.length
                      }
                      onChange={() => {
                        const allIds = table
                          .getCoreRowModel()
                          .rows.map((row) =>
                            checkboxSelection.getRowId(row.original)
                          )
                        if (
                          checkboxSelection.selectedRows.size === allIds.length
                        ) {
                          checkboxSelection.setSelectedRows(new Set())
                        } else {
                          checkboxSelection.setSelectedRows(new Set(allIds))
                        }
                      }}
                    />
                  </TableTh>
                )}
                {table
                  .getLeftLeafHeaders()
                  .concat(table.getCenterLeafHeaders())
                  .map((header) => (
                    <TableTh
                      key={header.id}
                      onClick={() =>
                        header.column.toggleSorting(undefined, true)
                      }
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Text fw={700} fz="sm">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Text>
                        {header.column.getCanSort() && (
                          <Group w={24}>
                            <SortIcon sortOrder={header.column.getIsSorted()} />
                          </Group>
                        )}
                      </Group>
                    </TableTh>
                  ))}
              </TableTr>
            </TableThead>
            <TableTbody>
              {table.getRowModel().rows.map((row) => (
                <TableTr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  style={{
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                >
                  {checkboxSelection && (
                    <TableTd onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={checkboxSelection.selectedRows.has(
                          checkboxSelection.getRowId(row.original)
                        )}
                        onChange={() =>
                          handleRowSelect(
                            checkboxSelection.getRowId(row.original)
                          )
                        }
                      />
                    </TableTd>
                  )}
                  {row
                    .getLeftVisibleCells()
                    .concat(row.getCenterVisibleCells())
                    .map((cell) => {
                      const isPinned = cell.column.getIsPinned() === "left"
                      return (
                        <TableTd
                          key={cell.id}
                          className={`${isPinned ? classes.isPinned : ""}`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableTd>
                      )
                    })}
                </TableTr>
              ))}

              {table.getRowModel().rows.length === 0 && (
                <TableTr>
                  <TableTd colSpan={table.getAllLeafColumns().length}>
                    <Container mt={40} style={{ marginLeft: 40 }}>
                      <Alert
                        color="gray"
                        icon={<IconInfoCircle />}
                        title={
                          hasFilters
                            ? t("lists.noSearchResults.title")
                            : t("lists.noData.title")
                        }
                      >
                        {hasFilters
                          ? t("lists.noSearchResults.message")
                          : t("lists.noData.message")}
                      </Alert>
                    </Container>
                  </TableTd>
                </TableTr>
              )}
            </TableTbody>
          </MantineTable>
        </ScrollArea>
      </Card>
      {pagination ? (
        <Group justify="right">
          <Pagination
            size="sm"
            value={currentPage}
            total={table.getPageCount()}
            onChange={handleChangePage}
          />
        </Group>
      ) : null}
    </Stack>
  )
}
